/**
 * CBS-portal presentation tokens for the internal Accounts view.
 *
 * Mirrors ~/cbs-portal's Accounts UI accents (StageTag phase pills and
 * TenantBadge tints) so the internal portal reads like the CBS reference
 * design. Consumed only behind `useIsInternal()` branches — client/tenant
 * portals never read these tokens.
 */

import type {CSSProperties} from 'react';
import type {LifecyclePhase} from '@api/generated/client';
import type {AccountsSubStageKey} from '@common/accountsView';

/** Phase accent colors from cbs-portal's `PHASE_PILL_COLORS`. */
export const CBS_PHASE_PILL_COLORS: Record<LifecyclePhase, string> = {
  LEAD: '#64748B',
  PROPOSAL: '#2563EB',
  ONBOARDING: '#D97706',
  IMPLEMENTATION: '#7C3AED',
  ACTIVE: '#0D9488',
};

/** Lost/archived accounts render in the CBS error red. */
export const CBS_LOST_PILL_COLOR = '#DC2626';

/**
 * Inline style for a cbs-portal StageTag pill: a 14% tint of the phase color
 * on white, with the full-strength color as the text.
 */
export function cbsStagePillStyle(color: string): CSSProperties {
  return {
    backgroundColor: `color-mix(in srgb, ${color} 14%, white)`,
    color,
  };
}

/** Class string shared by every cbs StageTag pill. */
export const CBS_STAGE_PILL_CLASS =
  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap';

interface TenantTint {
  bg: string;
  text: string;
}

// Known tenants keep cbs-portal's exact TenantBadge tints; anything else gets
// a deterministic pick from the same palette family.
const KNOWN_TENANT_TINTS: Record<string, TenantTint> = {
  synrgy: {bg: '#EFF6FF', text: '#2563EB'},
  champ: {bg: '#FEF3C7', text: '#D97706'},
  quantum: {bg: '#F3E8FF', text: '#7C3AED'},
  healthcues: {bg: '#ECFDF5', text: '#059669'},
};

const TENANT_TINT_PALETTE: TenantTint[] = [
  {bg: '#EFF6FF', text: '#2563EB'}, // blue
  {bg: '#FEF3C7', text: '#D97706'}, // amber
  {bg: '#F3E8FF', text: '#7C3AED'}, // violet
  {bg: '#ECFDF5', text: '#059669'}, // emerald
  {bg: '#CCFBF1', text: '#0F766E'}, // teal
  {bg: '#FEE2E2', text: '#B91C1C'}, // red
];

/**
 * Deterministic tenant-badge tint per client name, as an inline style. Known
 * CBS tenants match cbs-portal's TenantBadge exactly; other tenants hash into
 * the same palette.
 */
export function cbsTenantTint(name: string): CSSProperties {
  const key = name.trim().toLowerCase().replace(/\s+/g, '');
  let tint = KNOWN_TENANT_TINTS[key];
  if (!tint) {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    tint = TENANT_TINT_PALETTE[hash % TENANT_TINT_PALETTE.length];
  }
  return {backgroundColor: tint.bg, color: tint.text};
}

/** Class string for the cbs TenantBadge pill. */
export const CBS_TENANT_BADGE_CLASS =
  'inline-flex items-center px-1.5 py-[1px] rounded-full text-xs font-semibold uppercase tracking-wide whitespace-nowrap';

/** cbs-portal's Live tile tones (Status of Accounts board / Live status pill). */
export const CBS_LIVE_TILE = {bg: '#DCFCE7', text: '#15803D'};

/**
 * cbs-portal's soft binary status-pill tones, shared by the internal member
 * search, member detail, VBA group detail, and their sub-components so the
 * hexes live in one place.
 */
export const CBS_ACTIVE_PILL_CLASS = 'bg-[#DCFCE7] text-[#166534]';
export const CBS_INACTIVE_PILL_CLASS = 'bg-[#FEE2E2] text-[#991B1B]';
/** The "Current" coverage-window pill (demo uses a distinct minty green). */
export const CBS_CURRENT_PILL_CLASS = 'bg-[#D1FAE5] text-[#047857]';

export interface CbsStatusTone {
  bg: string;
  fg: string;
}

/**
 * Per-status tones from cbs-portal's `STATUS_TONE` (Status of Accounts board):
 * greens for live/late-stage, blues/teals for active onboarding, ambers for
 * waits, red for the census exception. Keyed by canonical sub-stage; statuses
 * outside the CBS implementation pipeline fall back to their phase color via
 * `cbsStatusTone`.
 */
export const CBS_STATUS_TONE: Partial<Record<AccountsSubStageKey, CbsStatusTone>> = {
  ACTIVE: {bg: '#DCFCE7', fg: '#15803D'},
  GO_LIVE_RUN: {bg: '#CCFBF1', fg: '#0F766E'},
  DRY_RUN_2: {bg: '#E0F2FE', fg: '#075985'},
  FINANCIAL_CALL: {bg: '#E0F2FE', fg: '#075985'},
  DRY_RUN_1: {bg: '#E0F2FE', fg: '#075985'},
  IMPLEMENTATION_CALL: {bg: '#FEF3C7', fg: '#92400E'},
  CENSUS_APPROVED: {bg: '#DCFCE7', fg: '#166534'},
  CENSUS_UPDATE_NEEDED: {bg: '#FEE2E2', fg: '#991B1B'},
  CENSUS_REVIEW: {bg: '#FEF3C7', fg: '#92400E'},
  CENSUS_BUILD_OUT: {bg: '#EDE9FE', fg: '#5B21B6'},
  WELCOME_CALL: {bg: '#FEF3C7', fg: '#92400E'},
  RFC_RECEIVED: {bg: '#DBEAFE', fg: '#1D4ED8'},
};

/**
 * Tone for a canonical sub-stage, falling back to a 14% tint of the given
 * lifecycle-phase color for sub-stages outside the CBS_STATUS_TONE catalog
 * (custom tenant pipeline steps, pre-implementation stages).
 */
export function cbsStatusTone(
  systemSubStage: AccountsSubStageKey,
  phase: LifecyclePhase,
): CbsStatusTone {
  const tone = CBS_STATUS_TONE[systemSubStage];
  if (tone) return tone;
  const color = CBS_PHASE_PILL_COLORS[phase];
  return {
    bg: `color-mix(in srgb, ${color} 14%, white)`,
    fg: `color-mix(in srgb, ${color} 85%, black)`,
  };
}
